<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import OperationalDashboard from './OperationalDashboard.vue';
import TeamDashboard from './TeamDashboard.vue';
import InboxSelector from './components/InboxSelector.vue';
import MultiSelectFilter from './components/MultiSelectFilter.vue';
import { useStore } from 'dashboard/composables/store';

const { t } = useI18n();
const store = useStore();

const TABS = computed(() => [
  { key: 'operational', name: t('REPORT_DASHBOARD.TABS.OPERATIONAL') },
  { key: 'team', name: t('REPORT_DASHBOARD.TABS.TEAM') },
]);

const selectedTabIndex = ref(0);
const selectedTabKey = computed(() => TABS.value[selectedTabIndex.value]?.key);
const selectedInboxId = ref(null);
const selectedTeamIds = ref([]);

// 0 nunca é um id de time real — usado como valor especial pra "sem time"
// (conversas com team_id nulo, que o backend também reconhece — live_reports
// e summary_reports/team). Nenhum item selecionado já significa "sem
// filtro" (mostra tudo), então não precisa de uma opção "Tudo" separada.
const NO_TEAM_ID = 0;

const allInboxes = computed(() => store.getters['inboxes/getInboxes']);
const allTeams = computed(() => [
  { id: NO_TEAM_ID, name: t('REPORT_DASHBOARD.TEAM_SELECTOR.NO_TEAM') },
  ...store.getters['teams/getTeams'].map(team => ({
    id: team.id,
    name: team.name,
  })),
]);

const onTabChange = index => {
  selectedTabIndex.value = index;
};

onMounted(() => {
  store.dispatch('inboxes/get');
  store.dispatch('teams/get');
});
</script>

<template>
  <div
    class="bg-n-background text-n-slate-12 h-full flex flex-col overflow-hidden"
  >
    <header class="relative text-center pt-6 pb-3 px-6 shrink-0">
      <div class="absolute left-6 top-6">
        <InboxSelector v-model="selectedInboxId" :inboxes="allInboxes" />
      </div>
      <h1 class="text-3xl lg:text-4xl font-bold uppercase tracking-widest">
        {{ t('REPORT_DASHBOARD.TITLE') }}
      </h1>
      <div class="absolute right-6 top-6">
        <MultiSelectFilter
          v-model="selectedTeamIds"
          :items="allTeams"
          :placeholder="t('REPORT_DASHBOARD.TEAM_SELECTOR.ALL_TEAMS')"
          :empty-state-message="
            t('REPORT_DASHBOARD.TEAM_SELECTOR.NO_TEAMS_FOUND')
          "
          align-right
          show-select-all
        />
      </div>
    </header>

    <div class="flex justify-center border-b border-n-weak shrink-0">
      <woot-tabs
        :index="selectedTabIndex"
        :border="false"
        @change="onTabChange"
      >
        <woot-tabs-item
          v-for="(tab, index) in TABS"
          :key="tab.key"
          :index="index"
          :name="tab.name"
          :show-badge="false"
        />
      </woot-tabs>
    </div>

    <div class="flex-1 min-h-0 flex flex-col">
      <OperationalDashboard
        v-if="selectedTabKey === 'operational'"
        :inbox-id="selectedInboxId"
        :team-ids="selectedTeamIds"
      />
      <TeamDashboard
        v-if="selectedTabKey === 'team'"
        :inbox-id="selectedInboxId"
        :team-ids="selectedTeamIds"
      />
    </div>
  </div>
</template>
